import { parseISO } from "date-fns";
import { ArrowRight, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { SectionCard } from "~/components/section-card";

import type { EntityEvidenceRow } from "~/features/entities/types";

interface EntityEvidencesBoxProps {
  rows: EntityEvidenceRow[];
}

export function EntityEvidencesBox({ rows }: EntityEvidencesBoxProps) {
  const { t } = useTranslation();

  if (rows.length === 0) return null;

  return (
    <SectionCard icon={FileText} title={t("model.evidence.modelNames")} contentClassName="overflow-x-auto p-0">
      <table className="w-full min-w-max border-collapse text-[13px]">
        <thead>
          <tr className="bg-bg-subtle">
            <Th>{t("model.common.id")}</Th>
            <Th>{t("ui.date")}</Th>
            <Th>{t("model.common.name")}</Th>
            <Th>{t("model.reference.modelName")}</Th>
            <Th>{t("model.user.modelName")}</Th>
            <Th>{t("model.common.updatedAt")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((ev) => (
            <tr key={ev.id} className="border-b border-border-default last:border-b-0">
              <td className="whitespace-nowrap px-3.5 py-2.5 text-fg-2">
                <span className="inline-flex items-center gap-1.5 tabular-nums">
                  {ev.id}
                  <Link to={`/evidences/${ev.id}`} aria-label={t("ui.detail")} className="text-fg-link hover:underline">
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </span>
              </td>
              <td className="whitespace-nowrap px-3.5 py-2.5 tabular-nums text-fg-2">{ev.date || "—"}</td>
              <td className="px-3.5 py-2.5 text-fg-1">{ev.name || "—"}</td>
              <td className="px-3.5 py-2.5 text-fg-2">
                {ev.reference ? (
                  <span className="inline-flex items-center gap-1.5" title={ev.reference.title}>
                    {ev.reference.shortCode || ev.reference.title}
                    <Link
                      to={`/references/${ev.reference.id}`}
                      aria-label={t("ui.detail")}
                      className="text-fg-link hover:underline"
                    >
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className="whitespace-nowrap px-3.5 py-2.5 text-fg-2">{ev.userName || t("ui.noUser")}</td>
              <td className="whitespace-nowrap px-3.5 py-2.5 tabular-nums text-fg-3">
                {ev.updatedAt ? t("date.long", { date: parseISO(ev.updatedAt) }) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="border-b border-border-default px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-fg-2">
      {children}
    </th>
  );
}
